/**
 * Active Page Context Store
 *
 * Mirrors the current `PageActionContext` (normally scoped to the nearest
 * `PageActionProvider`) into a global Zustand store so that app-root
 * components which are siblings of the page tree — not descendants of it —
 * can read "what page/entity is currently on screen".
 *
 * `PageActionProvider` writes into this store on every context change.
 * Consumers (e.g. the assistant overlay) read it reactively via
 * `useActivePageContext()`.
 *
 * No domain logic — pure infrastructure.
 */

import { create } from 'zustand';
import type { PageActionContext } from '../../types/page-actions';

interface ActivePageContextState {
  context: PageActionContext | null;
  setContext: (context: PageActionContext) => void;
}

export const useActivePageContextStore = create<ActivePageContextState>()((set) => ({
  context: null,
  setContext: (context) => set({ context }),
}));

/** Read the current page's context reactively (re-renders on change). */
export function useActivePageContext(): PageActionContext | null {
  return useActivePageContextStore((s) => s.context);
}
